// UnpublishedPostCard.jsx
import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { format, fromUnixTime } from 'date-fns';

import { SanitizedLink as Link } from '../component/SanitizedLink.jsx';
import Loading, { LoadingType } from '../component/Loading.jsx';
import StatefulImage from '../component/StatefulImage.jsx';
import LazyImage, { Image } from '../component/LazyImage.jsx';
import CircleProgressBar from '../component/CircleProgressBar.jsx';

import DecryptionWrapper from '../container/DecryptionWrapper.js';
import UploadModalMediaThumbnail from '../container/UploadModalMediaThumbnail.js';

import { TranslationNamespace } from '../resource/translationNamespace.js';
import { getIsInBrowserMainThread } from '../resource/getJsEnvironment.js';
import {
  getPoster,
  MediaPreset,
  MediaAssetFormat,
} from '../resource/getMediaAsset.js';
import numberWithCommas from '../resource/numberWithCommas.js';
import { ButtonId } from '../resource/trackingConstants.js';
import { ProfileListPublishStatus } from '../resource/profileConstants.js';

import media from '../style/media.js';
import { color } from '../style/variables.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';

import DiamondIconSource from '../../img/ic_diamond_xs.svg';
import CloseIconSource from '../../img/ic_close_white.svg';
import ErrorIconSource from '../../img/ic_error.svg';
import PhotoIconSource from '../../img/ic_inbox_photo.svg';
import VideoIconSource from '../../img/ic_inbox_video.svg';
import StoryIconSource from '../../img/ic_timer.svg';
import ShortsIconSource from '../../img/ic_short_white_fill.svg';

const isClient = getIsInBrowserMainThread();

const MessageType = {
  STORY: 'story',
  SHORTS: 'shorts',
  POST: 'post',
};

export const UnpublishedPostCard = ({
  messageId = null,
  meId = null,
  senderId = null,
  assetId = null,
  originalTitle = '',
  originalCaption = '',
  statusI18nKey = '',
  uploadJobId = null,
  contentType = '',
  createdAtUnix = null,
  postedAtUnix = null,
  scheduledAtUnix = null,
  ttl = null,
  unlockPrice = null,
  duration = null,
  progress = 0,
  shortVideoDuration = 0,
  isLocal = false,
  isReviewing = false,
  isFailed = false,
  isDeletable = false,
  isUpload = false,
  toggleModal = () => null,
  pauseUploadJobs = () => null,
  subscribePresenceMessage = () => null,
}) => {
  const shouldShowUnlockPrice = unlockPrice != null && unlockPrice > 0;
  const isScheduledPost = scheduledAtUnix != null;
  const isSystemGenerated = createdAtUnix != null && postedAtUnix == null;
  const isVideo = useMemo(
    () => contentType.startsWith('video/'),
    [contentType]
  );
  const isImage = useMemo(
    () => contentType.startsWith('image/'),
    [contentType]
  );
  const { t } = useTranslation();
  const [posterJpg, posterWebp, posterAvif] = useMemo(() => {
    return [
      [128, MediaAssetFormat.JPG],
      [128, MediaAssetFormat.WEBP],
      [128, MediaAssetFormat.AVIF],
    ].map(([size, format]) =>
      getPoster({
        id: messageId,
        meId,
        userId: senderId,
        size: `${size}x${size}`,
        format,
        preset: MediaPreset.SD,
      })
    );
  }, [meId, messageId, senderId]);
  const messageType = useMemo(() => {
    if (ttl != null) return MessageType.STORY;
    // TODO: refactor this to re-use logic
    if (isVideo && shortVideoDuration && duration < shortVideoDuration)
      return MessageType.SHORTS;
    return MessageType.POST;
  }, [duration, isVideo, shortVideoDuration, ttl]);
  const MetaIconsComponent = useMemo(() => {
    switch (messageType) {
      case MessageType.STORY: {
        return (
          <MetaIcons>
            {isVideo && <MetaIcon src={VideoIconSource} alt="video icon" />}
            {isImage && <MetaIcon src={PhotoIconSource} alt="photo icon" />}
            <MetaIcon src={StoryIconSource} alt="story icon" />
          </MetaIcons>
        );
      }
      case MessageType.SHORTS: {
        return (
          <MetaIcons>
            <MetaIcon src={ShortsIconSource} alt="shorts icon" />
          </MetaIcons>
        );
      }
      default: {
        return (
          <MetaIcons>
            {isVideo && <MetaIcon src={VideoIconSource} alt="video icon" />}
            {isImage && <MetaIcon src={PhotoIconSource} alt="photo icon" />}
          </MetaIcons>
        );
      }
    }
  }, [isImage, isVideo, messageType]);
  const unpublishedCardProps = useMemo(() => {
    if (isLocal)
      return {
        'data-element_id': ButtonId.Profile.ButtonUnpublishedCard,
        'data-tracking_payload': {
          messageId,
        },
        onClick: () => {
          toggleModal({
            modalId: 'UploadJobMoreModal',
            isOpen: true,
            payload: {
              uploadJobId,
            },
          });
        },
      };
    return {
      as: Link,
      to:
        MessageType.SHORTS === messageType
          ? `/shorts/${messageId}/${ProfileListPublishStatus.DRAFT}`
          : MessageType.STORY === messageType
            ? `/story/${messageId}`
            : `/post/${messageId}`,
      'data-element_id': ButtonId.Profile.ButtonUnpublishedCard,
      'data-tracking_payload': {
        messageId,
      },
    };
  }, [isLocal, messageId, messageType, toggleModal, uploadJobId]);
  // Subscribe `presence-message` channel if is unpublished post.
  useEffect(() => {
    if (!isLocal) {
      subscribePresenceMessage({ messageId, uploadJobId });
    }
  }, [isLocal, messageId, subscribePresenceMessage, uploadJobId]);
  return (
    <StyledUnpublishedPost {...unpublishedCardProps}>
      <Preview>
        {isLocal ? (
          <>
            <UploadModalMediaThumbnail assetId={assetId} />
            {isFailed ? (
              <ErrorIcon>
                <img src={ErrorIconSource} alt="error icon" />
              </ErrorIcon>
            ) : isDeletable ? (
              <DeleteButton
                data-element_id={ButtonId.All.ButtonDelete}
                data-tracking_payload={{
                  messageId,
                }}
                onClick={event => {
                  event.stopPropagation();
                  pauseUploadJobs({
                    uploadJobIds: [uploadJobId],
                  });
                }}
              >
                <CircleProgressBar value={progress} barColor={color.tealBlue} />
                <img src={CloseIconSource} alt="delete icon" />
              </DeleteButton>
            ) : (
              <LoadingWrapper>
                <Loading type={LoadingType.GRAY} />
              </LoadingWrapper>
            )}
          </>
        ) : (
          <StatefulImage>
            <Loading data-key="error" />
            <Loading data-key="loading" />
            <LazyImage data-key="target" threshold={0.25}>
              <DecryptionWrapper resourceUrl={posterJpg.href}>
                <source srcSet={`${posterAvif} 1x`} />
                <source srcSet={`${posterWebp} 1x`} />
                <Image srcSet={`${posterJpg.href} 1x`} src={posterJpg.href} />
              </DecryptionWrapper>
            </LazyImage>
          </StatefulImage>
        )}
      </Preview>
      <Title>{originalTitle || originalCaption}</Title>
      <Time
        color={
          isReviewing ? color.sky : isFailed ? color.red : color.neutral[30]
        }
      >
        {MetaIconsComponent}
        {isLocal
          ? `${t(statusI18nKey, {
              ns: TranslationNamespace.POST,
            })}${isUpload ? ` ${progress}%` : ''}`
          : isScheduledPost
            ? t('schedule_time', {
                ns: TranslationNamespace.POST,
                date:
                  /* render formated time on SSR will causing hydration fail due to timezone */
                  isClient && scheduledAtUnix
                    ? format(fromUnixTime(scheduledAtUnix), 'yyyy-MM-dd HH:mm')
                    : '',
              })
            : isSystemGenerated
              ? t('system_auto_generate', {
                  ns: TranslationNamespace.POST,
                  date:
                    /* render formated time on SSR will causing hydration fail due to timezone */
                    isClient && createdAtUnix
                      ? format(fromUnixTime(createdAtUnix), 'yyyy-MM-dd HH:mm')
                      : '',
                })
              : null}
      </Time>
      {shouldShowUnlockPrice && (
        <Price>
          <img src={DiamondIconSource} alt="diamond" />
          {numberWithCommas({ number: unlockPrice })}
        </Price>
      )}
    </StyledUnpublishedPost>
  );
};

UnpublishedPostCard.propTypes = {
  messageId: PropTypes.string,
  meId: PropTypes.string,
  senderId: PropTypes.string,
  assetId: PropTypes.string,
  originalTitle: PropTypes.string,
  originalCaption: PropTypes.string,
  statusI18nKey: PropTypes.string,
  uploadJobId: PropTypes.string,
  contentType: PropTypes.string,
  createdAtUnix: PropTypes.number,
  postedAtUnix: PropTypes.number,
  scheduledAtUnix: PropTypes.number,
  ttl: PropTypes.number,
  unlockPrice: PropTypes.number,
  duration: PropTypes.number,
  progress: PropTypes.number,
  shortVideoDuration: PropTypes.number,
  isLocal: PropTypes.bool,
  isReviewing: PropTypes.bool,
  isFailed: PropTypes.bool,
  isDeletable: PropTypes.bool,
  isUpload: PropTypes.bool,
  toggleModal: PropTypes.func,
  pauseUploadJobs: PropTypes.func,
  subscribePresenceMessage: PropTypes.func,
};

const StyledUnpublishedPost = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid rgba(100, 100, 100, 0.15);
  width: 100%;
  display: grid;
  grid-template-areas:
    'preview title title'
    'preview time price';
  grid-template-columns: 48px 1fr auto;
  grid-column-gap: 12px;
  grid-row-gap: 4px;
  cursor: pointer;
  ${media.tablet`
    padding: 8px 16px;
    grid-template-columns: 44px 1fr auto;
  `};
  ${media.mobile`
    grid-template-columns: 40px 1fr auto;
  `};
`;

const Preview = styled.div`
  position: relative;
  width: 48px;
  height: 48px;
  grid-area: preview;
  background-color: ${color.grey['300']};
  ${media.tablet`
    width: 44px;
    height: 44px;
  `};
  ${media.mobile`
    width: 40px;
    height: 40px;
  `};
`;

const PreviewOverlay = css`
  position: absolute;
  top: 0px;
  bottom: 0px;
  left: 0px;
  right: 0px;
  background-color: rgba(25, 25, 25, 0.8);
`;

const ErrorIcon = styled.div`
  ${PreviewOverlay};
  display: flex;
  align-items: center;
  justify-content: center;
  > img {
    width: 28px;
    height: 28px;
    object-fit: contain;
  }
`;

const DeleteButton = styled.button`
  ${ResetButtonStyle};
  ${PreviewOverlay};
  display: flex;
  align-items: center;
  justify-content: center;
  > img {
    position: absolute;
    display: block;
    width: 20px;
    height: 20px;
    object-fit: contain;
  }
`;

const LoadingWrapper = styled.div`
  ${PreviewOverlay};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Title = styled.div`
  grid-area: title;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: ${color.neutral[100]};
  font-size: 18px;
  line-height: 1.5;
  ${media.tablet`
    font-size: 16px;
  `};
  ${media.mobile`
    font-size: 14px;
  `};
`;

const Time = styled.div.attrs(({ color }) => ({
  style: {
    color,
  },
}))`
  grid-area: time;
  display: flex;
  align-items: center;
  color: ${color.neutral[30]};
  font-size: 16px;
  line-height: 1.5;
  ${media.tablet`
    font-size: 12px;
  `};
`;

const MetaIcons = styled.div`
  margin-right: 4px;
  display: flex;
  align-items: center;
  :empty {
    margin-right: 0px;
  }
`;

const MetaIcon = styled.img`
  margin-right: 2px;
  width: 16px;
  height: 16px;
  object-fit: contain;
  :last-child {
    margin-right: 0px;
  }
`;

const Price = styled.div`
  grid-area: price;
  display: flex;
  align-items: center;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.5;
  color: ${color.tealBlue};
  > img {
    margin-right: 4px;
    width: 20px;
    height: 20px;
    object-fit: contain;
  }
  ${media.tablet`
    font-size: 14px;
    > img {
      width: 12px;
      height: 12px;
      object-fit: contain;
    }
  `};
  ${media.mobile`
    font-size: 12px;
  `};
`;

export default UnpublishedPostCard;
